// ── RETAINERS SECTION ─────────────────────────────────────────────────────────
// Per-project retainer ledger. Mounts under InvoicesTab → Retainers.
//
// The ledger is the source of truth for the retainer balance. Deposits add,
// draws (Billing Summaries marked Paid) subtract. The current balance is
// always sum(amount) — never stored as a single field, always re-derivable.
//
// Only projects with hasRetainer=true appear here. Toggle on the project
// from Project Directory if you want a project to start tracking retainer.

import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { calcRetainerBalance, retainerEntriesWithRunningBalance, fmtMoney, hasAdminAccess } from '../utils';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    // YYYY-MM-DD (date column) — parse as local, not UTC, to avoid the
    // tz-shift that makes "2025-11-25" display as Nov 24 in PST.
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return String(iso).slice(0, 10); }
}

var inp = {
  padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6,
  fontSize: 13, fontFamily: 'inherit', outline: 'none', background: '#fff',
  boxSizing: 'border-box',
};
var lbl = { fontSize: 10, fontWeight: 600, color: '#888', display: 'block', marginBottom: 4, letterSpacing: '0.07em', textTransform: 'uppercase' };
var btnGhost = { height: 32, padding: '0 14px', borderRadius: 6, border: '0.5px solid #D8D7D2', background: '#FFFFFF', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#111111', fontFamily: 'inherit' };
var btnPrimary = { height: 32, padding: '0 16px', borderRadius: 6, border: '0.5px solid #111111', background: '#111111', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#FFFFFF', fontFamily: 'inherit' };
var btnDanger = { height: 28, padding: '0 12px', borderRadius: 5, border: '0.5px solid #C8322A', background: '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#C8322A', fontFamily: 'inherit' };

var TYPE_LABEL = { deposit: 'Deposit', draw: 'Draw', adjustment: 'Adjustment' };
var TYPE_COLOR = { deposit: '#1A5C38', draw: '#C8322A', adjustment: '#888880' };

export default function RetainersSection() {
  var ctx = useAppContext();
  var projects = ctx.projects || [];
  var retainerLedger = ctx.retainerLedger || [];
  var saveRetainerEntry = ctx.saveRetainerEntry;
  var saveRetainerEntryToDb = ctx.saveRetainerEntryToDb;
  var deleteRetainerEntry = ctx.deleteRetainerEntry;
  var userEmail = ctx.userEmail || '';
  var isOwner = ctx.isOwner;
  var canManage = isOwner || hasAdminAccess(userEmail, ctx.firmSettings);
  var sbs = useState(false);            var saving = sbs[0];        var setSaving = sbs[1];

  var sps = useState('');               var selProjLabel = sps[0];   var setSelProjLabel = sps[1];
  var ams = useState(false);            var addModal = ams[0];       var setAddModal = ams[1];
  var ats = useState('deposit');        var addType = ats[0];        var setAddType = ats[1];
  var ads = useState(function(){return new Date().toISOString().slice(0,10);}); var addDate = ads[0]; var setAddDate = ads[1];
  var ams2 = useState('');              var addAmount = ams2[0];     var setAddAmount = ams2[1];
  var ars = useState('');               var addRef = ars[0];         var setAddRef = ars[1];
  var ans = useState('');               var addNotes = ans[0];       var setAddNotes = ans[1];
  var apms = useState('');              var addMethod = apms[0];     var setAddMethod = apms[1];
  var abrs = useState('');              var addBankRef = abrs[0];    var setAddBankRef = abrs[1];
  var aes = useState('');               var addErr = aes[0];         var setAddErr = aes[1];
  var eis = useState(null);             var editingEntry = eis[0];   var setEditingEntry = eis[1];

  var retainerProjects = useMemo(function() {
    return projects.filter(function(p) { return !!p.hasRetainer; });
  }, [projects]);

  var projectBalances = useMemo(function() {
    var rows = retainerProjects.map(function(p) {
      var bal = calcRetainerBalance(retainerLedger, p.label);
      var entries = retainerLedger.filter(function(e) { return e.projectLabel === p.label; });
      return { label: p.label, project: p, balance: bal, count: entries.length };
    });
    return rows;
  }, [retainerProjects, retainerLedger]);

  var selProject = retainerProjects.find(function(p) { return p.label === selProjLabel; });
  var ledgerForProject = useMemo(function() {
    if (!selProjLabel) return [];
    return retainerEntriesWithRunningBalance(retainerLedger, selProjLabel).slice().reverse();
  }, [retainerLedger, selProjLabel]);

  function openAdd(type) {
    setEditingEntry(null);
    setAddType(type);
    setAddDate(new Date().toISOString().slice(0, 10));
    setAddAmount('');
    setAddRef('');
    setAddNotes('');
    setAddMethod('');
    setAddBankRef('');
    setAddErr('');
    setAddModal(true);
  }

  function openEdit(entry) {
    setEditingEntry(entry);
    setAddType(entry.entryType || 'deposit');
    setAddDate(entry.entryDate || new Date().toISOString().slice(0, 10));
    setAddAmount(String(Math.abs(parseFloat(entry.amount) || 0)));
    setAddRef(entry.reference || '');
    setAddNotes(entry.notes || '');
    setAddMethod(entry.paymentMethod || '');
    setAddBankRef(entry.bankReference || '');
    setAddErr('');
    setAddModal(true);
  }

  async function submitAdd() {
    setAddErr('');
    if (!selProjLabel) { setAddErr('Pick a project first.'); return; }
    // Strip $/commas/spaces and normalize typographic minus signs (U+2212,
    // en/em dash — common when an amount is pasted from email or chat) to a
    // plain hyphen so parseFloat doesn't reject an otherwise-valid number.
    var cleaned = String(addAmount || '').replace(/[−–—]/g, '-').replace(/[$,\s]/g, '');
    var amt = parseFloat(cleaned);
    if (!amt || isNaN(amt)) { setAddErr('Enter an amount.'); return; }
    if (addType === 'deposit' && amt < 0) { setAddErr('Deposit amount must be positive.'); return; }
    if (addType === 'draw' && amt > 0) amt = -Math.abs(amt);
    if (addType === 'deposit') amt = Math.abs(amt);
    var base = editingEntry ? Object.assign({}, editingEntry) : {
      sourceType: 'manual',
      createdBy: userEmail,
    };
    var entry = saveRetainerEntry(Object.assign(base, {
      projectLabel:  selProjLabel,
      entryDate:     addDate,
      entryType:     addType,
      amount:        amt,
      reference:     addRef,
      notes:         addNotes,
      paymentMethod: addMethod,
      bankReference: addBankRef,
    }));
    setSaving(true);
    try {
      var ok = await saveRetainerEntryToDb(entry);
      setSaving(false);
      if (!ok) {
        setAddErr('Saved locally but the database rejected it. Has the retainer_ledger migration been run in Supabase? See supabase/migrations/20260425_create_retainer_ledger_table.sql.');
        return;
      }
      setAddModal(false);
    } catch (e) {
      setSaving(false);
      setAddErr('Save failed: ' + (e.message || 'unknown error'));
    }
  }

  function removeEntry(entry) {
    if (!window.confirm('Delete this ' + (TYPE_LABEL[entry.entryType] || 'entry') + ' of ' + fmtMoney(entry.amount) + '?')) return;
    deleteRetainerEntry(entry.id);
  }

  // ── Project list view ──
  if (!selProjLabel) {
    return (
      <div style={{ padding: '20px 18px', maxWidth: 900 }}>
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111', marginBottom: 4 }}>Retainers</div>
          <div style={{ fontSize: 12, color: '#888', lineHeight: 1.5 }}>
            Per-project ledger of deposits and draws. The current balance is the running sum of all entries — every change is recorded with date, type, and reference for audit.
          </div>
        </div>

        {retainerProjects.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 8 }}>
            No projects on retainer. Toggle <strong style={{ fontStyle: 'normal' }}>Has Retainer</strong> on a project from the Projects directory to start tracking.
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#F7F7F5', borderBottom: '0.5px solid #E0E0DC' }}>
                  <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Project</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Entries</th>
                  <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {projectBalances.map(function(row) {
                  return (
                    <tr key={row.label}
                      onClick={function() { setSelProjLabel(row.label); }}
                      style={{ cursor: 'pointer', borderBottom: '0.5px solid #EFEFEC' }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = '#FAFAF8'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = '#FFFFFF'; }}>
                      <td style={{ padding: '12px 14px', fontWeight: 500, color: '#111' }}>{row.label}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', color: '#888' }}>{row.count}</td>
                      <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 500, color: row.balance < 0 ? '#C8322A' : '#111' }}>{fmtMoney(row.balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ── Single-project ledger view ──
  var balance = calcRetainerBalance(retainerLedger, selProjLabel);

  return (
    <div style={{ padding: '20px 18px', maxWidth: 1000 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button onClick={function() { setSelProjLabel(''); }} style={btnGhost}>← All retainers</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>{selProjLabel}</div>
          <div style={{ fontSize: 11, color: '#888' }}>Retainer ledger</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>Current Balance</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: balance < 0 ? '#C8322A' : '#1A5C38' }}>{fmtMoney(balance)}</div>
        </div>
      </div>

      {canManage && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button onClick={function() { openAdd('deposit'); }} style={btnPrimary}>+ Add Retainer Payment</button>
          {/* Manual draw entry (2026-07-08). Draws are normally created by
              the system when a billing summary is sent (draw-at-send,
              2026-07-01), but packages sent before that fix — and
              corrections/backfills — need a first-class manual path.
              Pre-fix the only workaround was a negative Adjustment, which
              mislabels the entry's economic meaning in the ledger. */}
          <button onClick={function() { openAdd('draw'); }} style={btnGhost}>+ Add Draw</button>
          <button onClick={function() { openAdd('adjustment'); }} style={btnGhost}>+ Adjustment</button>
        </div>
      )}

      {/* Ledger table */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' }}>
        {ledgerForProject.length === 0 ? (
          <div style={{ padding: '40px 20px', textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic' }}>
            No entries yet. Add the first retainer deposit to start tracking the balance.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#F7F7F5', borderBottom: '0.5px solid #E0E0DC' }}>
                <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Date</th>
                <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Type</th>
                <th style={{ padding: '10px 14px', textAlign: 'left',  fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Reference</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Amount</th>
                <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: 10, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Balance</th>
                {canManage && <th style={{ padding: '10px 14px', width: 50 }}></th>}
              </tr>
            </thead>
            <tbody>
              {ledgerForProject.map(function(e) {
                var clickable = canManage;
                return (
                  <tr key={e.id}
                    onClick={clickable ? function() { openEdit(e); } : undefined}
                    style={{ borderBottom: '0.5px solid #EFEFEC', cursor: clickable ? 'pointer' : 'default' }}
                    onMouseEnter={clickable ? function(ev) { ev.currentTarget.style.background = '#FAFAF8'; } : undefined}
                    onMouseLeave={clickable ? function(ev) { ev.currentTarget.style.background = '#FFFFFF'; } : undefined}>
                    <td style={{ padding: '10px 14px', color: '#555' }}>{fmtDate(e.entryDate)}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: TYPE_COLOR[e.entryType] || '#888' }} />
                        <span style={{ color: '#111' }}>{TYPE_LABEL[e.entryType] || e.entryType}</span>
                        {e.sourceType && e.sourceType !== 'manual' && (
                          <span style={{ fontSize: 10, color: '#888' }}>· auto</span>
                        )}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#555' }}>
                      <div>{e.reference || '—'}</div>
                      {e.notes && <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{e.notes}</div>}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 500, color: e.amount < 0 ? '#C8322A' : '#111' }}>
                      {fmtMoney(e.amount)}
                    </td>
                    <td style={{ padding: '10px 14px', textAlign: 'right', color: '#555' }}>
                      {fmtMoney(e.runningBalance)}
                    </td>
                    {canManage && (
                      <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                        <button onClick={function(ev) { ev.stopPropagation(); removeEntry(e); }} style={btnDanger}>Delete</button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add modal */}
      {addModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 460, maxWidth: '92vw', background: '#fff', borderRadius: 8, padding: 22, boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#111', marginBottom: 4 }}>
              {editingEntry
                ? 'Edit ' + (TYPE_LABEL[addType] || 'Entry')
                : addType === 'deposit' ? 'Add Retainer Payment' : addType === 'draw' ? 'Add Draw' : 'Add Adjustment'}
            </div>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 16 }}>{selProjLabel}</div>

            {/* Type is locked per the button you clicked (Add Retainer
                Payment / + Adjustment). The header already says which.
                Removed the dropdown so a deposit can never be filed as a
                draw by an accidental select-change. To change the type
                of an existing entry, delete it and re-create. */}
            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Date</label>
              <input type="date" value={addDate} onChange={function(e) { setAddDate(e.target.value); }} style={Object.assign({}, inp, { width: '100%' })} />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>
                Amount {addType === 'adjustment' ? '(positive or negative)' : addType === 'draw' ? '(entered as positive — saved as negative)' : ''}
              </label>
              <input type="text" inputMode="decimal" placeholder="0.00" value={addAmount} onChange={function(e) { setAddAmount(e.target.value); }} style={Object.assign({}, inp, { width: '100%' })} />
            </div>

            {/* Payment method + bank reference only apply to deposits
                (real cash events). Adjustments are bookkeeping corrections
                — no payment associated. Auto-created draws skip this
                modal entirely. */}
            {addType === 'deposit' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
                <div>
                  <label style={lbl}>Payment method</label>
                  <select value={addMethod} onChange={function(e) { setAddMethod(e.target.value); }}
                    style={Object.assign({}, inp, { width: '100%', background: '#fff' })}>
                    <option value="">— Pick —</option>
                    <option value="Wire">Wire</option>
                    <option value="Check">Check</option>
                    <option value="ACH">ACH</option>
                    <option value="Zelle">Zelle</option>
                    <option value="Credit card">Credit card</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Bank reference</label>
                  <input placeholder="Wire confirmation # / check #" value={addBankRef} onChange={function(e) { setAddBankRef(e.target.value); }} style={Object.assign({}, inp, { width: '100%' })} />
                </div>
              </div>
            )}

            <div style={{ marginBottom: 12 }}>
              <label style={lbl}>Reference</label>
              <input placeholder="e.g. April retainer / ABD transfer" value={addRef} onChange={function(e) { setAddRef(e.target.value); }} style={Object.assign({}, inp, { width: '100%' })} />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Notes</label>
              <textarea value={addNotes} onChange={function(e) { setAddNotes(e.target.value); }}
                style={Object.assign({}, inp, { width: '100%', minHeight: 60, resize: 'vertical' })} />
            </div>

            {addErr && (
              <div style={{ marginBottom: 12, padding: '8px 12px', background: '#FDECEA', border: '0.5px solid #F2A39C', color: '#C8322A', fontSize: 11, borderRadius: 4 }}>{addErr}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={function() { setAddModal(false); }} style={btnGhost} disabled={saving}>Cancel</button>
              <button onClick={submitAdd} style={btnPrimary} disabled={saving}>
                {saving ? 'Saving…' : 'Save Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
